/**
 * Slate.js Editor with Yjs CRDT for conflict-free collaboration
 * Uses binary Yjs updates instead of individual Slate operations
 */

import { Slate, Editable, withReact, RenderLeafProps, RenderElementProps } from 'slate-react'
import { createEditor, Editor as SlateEditor, Transforms, Element as SlateElement, BaseEditor, Descendant } from 'slate'
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactEditor } from 'slate-react'
import { withYjs, YjsEditor, withCursors, slateNodesToInsertDelta } from '@slate-yjs/core'
import * as Y from 'yjs'
import {
    Bold,
    Italic,
    Underline,
    Code,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Highlighter,
    Cloud,
    CloudOff,
    Loader2,
    CheckCircle2,
    Activity
} from 'lucide-react';
import { useNotes } from '../contexts/NotesContext';
import { useCommunication } from '@/lib/CommunicationWrapper';
import { CLOUDFLARE_URL } from '@/lib/config';

// Type definitions
type ParagraphElement = { type: 'paragraph'; align?: string; children: CustomText[] }
type HeadingElement = { type: 'heading-one' | 'heading-two' | 'heading-three'; align?: string; children: CustomText[] }
type BlockQuoteElement = { type: 'block-quote'; children: CustomText[] }
type BulletedListElement = { type: 'bulleted-list'; children: ListItemElement[] }
type NumberedListElement = { type: 'numbered-list'; children: ListItemElement[] }
type ListItemElement = { type: 'list-item'; children: CustomText[] }
type CodeBlockElement = { type: 'code-block'; children: CustomText[] }

type CustomElement =
    | ParagraphElement
    | HeadingElement
    | BlockQuoteElement
    | BulletedListElement
    | NumberedListElement
    | ListItemElement
    | CodeBlockElement

type CustomText = {
    text: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    code?: boolean;
    highlight?: boolean;
}

declare module 'slate' {
    interface CustomTypes {
        Editor: BaseEditor & ReactEditor & YjsEditor
        Element: CustomElement
        Text: CustomText
    }
}

// Helper functions
const toggleMark = (editor: SlateEditor, format: string) => {
    const isActive = isMarkActive(editor, format)
    if (isActive) {
        SlateEditor.removeMark(editor, format)
    } else {
        SlateEditor.addMark(editor, format, true)
    }
}

const isMarkActive = (editor: SlateEditor, format: string) => {
    const { selection } = editor;
    if (!selection) return false;

    const marks = SlateEditor.marks(editor) as CustomText | null;
    return marks ? marks[format as keyof CustomText] === true : false;
}

const toggleBlock = (editor: SlateEditor, format: string) => {
    const isActive = isBlockActive(editor, format)
    const isList = ['numbered-list', 'bulleted-list'].includes(format)

    Transforms.unwrapNodes(editor, {
        match: n => !SlateEditor.isEditor(n) && SlateElement.isElement(n) && ['numbered-list', 'bulleted-list'].includes(n.type),
        split: true,
    })

    const newProperties: Partial<SlateElement> = {
        type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    } as any

    Transforms.setNodes<SlateElement>(editor, newProperties)

    if (!isActive && isList) {
        const block = { type: format, children: [] } as any
        Transforms.wrapNodes(editor, block)
    }
}

const isBlockActive = (editor: SlateEditor, format: string) => {
    const { selection } = editor
    if (!selection) return false

    const [match] = Array.from(
        SlateEditor.nodes(editor, {
            at: SlateEditor.unhangRange(editor, selection),
            match: n => !SlateEditor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
        })
    )

    return !!match
}

// Toolbar Button Component
const ToolbarButton = ({
    active,
    onMouseDown,
    children,
    title
}: {
    active?: boolean;
    onMouseDown: (e: React.MouseEvent) => void;
    children: React.ReactNode;
    title: string;
}) => (
    <button
        onMouseDown={onMouseDown}
        title={title}
        className={`p-2.5 rounded-lg transition-all duration-200 ${active
            ? 'bg-theme-0 text-theme-link scale-95'
            : 'text-theme-1 hover:text-theme-0 hover:bg-theme-1'
            }`}
    >
        {children}
    </button>
)

// Floating Toolbar Component
const FloatingToolbar = ({ editorContainer, editor }: { editorContainer: RefObject<HTMLDivElement>, editor: SlateEditor & ReactEditor }) => {
    const toolbarRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        const updateToolbarPosition = () => {
            const { selection } = editor;
            const toolbar = toolbarRef.current;

            if (!toolbar || !selection || !ReactEditor.isFocused(editor)) {
                setIsVisible(false)
                return
            }

            const selectionText = SlateEditor.string(editor, selection)
            if (selectionText === '') {
                setIsVisible(false)
                return
            }

            try {
                const domRange = ReactEditor.toDOMRange(editor, selection)
                const rect = domRange.getBoundingClientRect()

                if (rect.width === 0 && rect.height === 0) {
                    setIsVisible(false)
                    return
                }

                setIsVisible(true)

                const editorEl = ReactEditor.toDOMNode(editor, editor)
                const editorRect = editorEl.getBoundingClientRect()
                const containerRect = editorContainer.current?.getBoundingClientRect() ?? editorRect;

                let top = rect.top - editorRect.top - toolbar.offsetHeight - 10 + 32;
                if (top <= (editorContainer.current?.scrollTop ?? 0)) {
                    top = rect.bottom - editorRect.top + toolbar.offsetHeight + 10;
                }

                const left = Math.min(containerRect.right - editorRect.left - toolbar.offsetWidth,
                    Math.max(containerRect.left - editorRect.left,
                        rect.left - editorRect.left + rect.width / 2 - toolbar.offsetWidth / 2))

                toolbar.style.top = `${top}px`
                toolbar.style.left = `${left}px`
            } catch (error) {
                console.error('Error getting DOM range:', error)
                setIsVisible(false)
                return
            }
        }

        updateToolbarPosition()

        const editorWindow = ReactEditor.getWindow(editor)
        const editorDocument = editorWindow.document

        const handleSelectionChange = () => {
            requestAnimationFrame(updateToolbarPosition)
        }

        editorDocument.addEventListener('selectionchange', handleSelectionChange)

        return () => {
            editorDocument.removeEventListener('selectionchange', handleSelectionChange)
        }
    }, [editor, editorContainer])

    return (
        <div
            ref={toolbarRef}
            className={`absolute z-50 backdrop-blur-2xl bg-theme-0/95 border border-theme rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] p-2 flex gap-0.5 transition-all duration-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'
                }`}
            onMouseDown={e => e.preventDefault()}
        >
            <ToolbarButton
                active={isMarkActive(editor, 'bold')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleMark(editor, 'bold')
                }}
                title="Bold"
            >
                <Bold className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isMarkActive(editor, 'italic')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleMark(editor, 'italic')
                }}
                title="Italic"
            >
                <Italic className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isMarkActive(editor, 'underline')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleMark(editor, 'underline')
                }}
                title="Underline"
            >
                <Underline className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isMarkActive(editor, 'code')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleMark(editor, 'code')
                }}
                title="Code"
            >
                <Code className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isMarkActive(editor, 'highlight')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleMark(editor, 'highlight')
                }}
                title="Highlight"
            >
                <Highlighter className="size-4" />
            </ToolbarButton>

            <div className="w-px h-6 bg-theme mx-1 my-auto" />

            <ToolbarButton
                active={isBlockActive(editor, 'heading-one')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleBlock(editor, 'heading-one')
                }}
                title="Heading 1"
            >
                <Heading1 className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isBlockActive(editor, 'heading-two')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleBlock(editor, 'heading-two')
                }}
                title="Heading 2"
            >
                <Heading2 className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isBlockActive(editor, 'heading-three')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleBlock(editor, 'heading-three')
                }}
                title="Heading 3"
            >
                <Heading3 className="size-4" />
            </ToolbarButton>

            <div className="w-px h-6 bg-theme mx-1 my-auto" />

            <ToolbarButton
                active={isBlockActive(editor, 'bulleted-list')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleBlock(editor, 'bulleted-list')
                }}
                title="Bulleted List"
            >
                <List className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isBlockActive(editor, 'numbered-list')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleBlock(editor, 'numbered-list')
                }}
                title="Numbered List"
            >
                <ListOrdered className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isBlockActive(editor, 'block-quote')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleBlock(editor, 'block-quote')
                }}
                title="Quote"
            >
                <Quote className="size-4" />
            </ToolbarButton>

            <ToolbarButton
                active={isBlockActive(editor, 'code-block')}
                onMouseDown={e => {
                    e.preventDefault()
                    toggleBlock(editor, 'code-block')
                }}
                title="Code Block"
            >
                <Code className="size-4" />
            </ToolbarButton>
        </div>
    )
}

// Sync Status type
type SyncStatus = 'saved' | 'saving' | 'error';

// Sync Status Indicator
const SyncStatusIndicator = ({ status }: { status: SyncStatus }) => {
    const getStatusDisplay = () => {
        switch (status) {
            case 'saving':
                return {
                    icon: <Activity className="size-3 animate-pulse" />,
                    text: 'Saving...',
                    color: 'blue'
                };
            case 'saved':
                return {
                    icon: <CheckCircle2 className="size-3" />,
                    text: 'Saved',
                    color: 'green'
                };
            case 'error':
                return {
                    icon: <CloudOff className="size-3" />,
                    text: 'Error (retrying...)',
                    color: 'red'
                };
        }
    };

    const display = getStatusDisplay();

    const colorClasses = {
        blue: 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400',
        green: 'bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400',
        red: 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400'
    };

    return (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${colorClasses[display.color as keyof typeof colorClasses]}`}>
            {display.icon}
            <span className="text-xs font-medium">{display.text}</span>
        </div>
    );
};

// Render components
const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
    if (leaf.bold) {
        children = <strong className="font-semibold">{children}</strong>
    }
    if (leaf.italic) {
        children = <em className="italic">{children}</em>
    }
    if (leaf.underline) {
        children = <u className="underline decoration-2 underline-offset-2">{children}</u>
    }
    if (leaf.code) {
        children = <code className="px-2 py-0.5 bg-theme-1 text-theme-link rounded-md font-mono text-[0.9em] border border-theme">{children}</code>
    }
    if (leaf.highlight) {
        children = <mark className="bg-yellow-300/40 dark:bg-yellow-400/20 px-1 rounded">{children}</mark>
    }
    return <span {...attributes}>{children}</span>
}

const Element = ({ attributes, children, element }: RenderElementProps) => {
    const style = { textAlign: ('align' in element ? element.align : undefined) || 'left' } as React.CSSProperties

    switch (element.type) {
        case 'heading-one':
            return <h1 {...attributes} style={style} className="text-3xl font-bold text-theme-0 mt-8 mb-3 tracking-tight">{children}</h1>
        case 'heading-two':
            return <h2 {...attributes} style={style} className="text-2xl font-semibold text-theme-0 mt-6 mb-2 tracking-tight">{children}</h2>
        case 'heading-three':
            return <h3 {...attributes} style={style} className="text-xl font-semibold text-theme-0 mt-5 mb-2">{children}</h3>
        case 'block-quote':
            return <blockquote {...attributes} className="border-l-[3px] border-theme-link pl-6 italic text-theme-1 my-6 py-1">{children}</blockquote>
        case 'bulleted-list':
            return <ul {...attributes} className="list-disc pl-6 my-4 text-theme-0 space-y-2">{children}</ul>
        case 'numbered-list':
            return <ol {...attributes} className="list-decimal pl-6 my-4 text-theme-0 space-y-2">{children}</ol>
        case 'list-item':
            return <li {...attributes} className="text-theme-0 leading-relaxed">{children}</li>
        case 'code-block':
            return <pre {...attributes} className="bg-theme-1 text-theme-0 p-4 rounded-xl my-6 overflow-x-auto font-mono text-sm border border-theme"><code>{children}</code></pre>
        default:
            return <p {...attributes} style={style} className="text-theme-0 leading-[1.8] my-3">{children}</p>
    }
}

/**
 * Main Yjs Editor Component
 */
export const EditorYjs = () => {
    const { noteId } = useNotes();
    const { authenticatedFetch } = useCommunication();

    const [syncStatus, setSyncStatus] = useState<SyncStatus>('saved');
    const [isLoading, setIsLoading] = useState(true);

    const editorContainer = useRef<HTMLDivElement>(null);
    const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // NEW: Buffer to store incremental updates (deltas)
    const pendingUpdatesRef = useRef<Uint8Array[]>([]);

    const ydoc = useMemo(() => new Y.Doc(), []);
    const sharedType = useMemo(() => ydoc.get('content', Y.XmlText), [ydoc]);
    const editor = useMemo(() => withReact(withYjs(createEditor(), sharedType)), [sharedType]);

    // 1. Capture incremental updates
    const handleUpdate = useCallback((update: Uint8Array, origin: any) => {
        if (origin === 'server') return;
        pendingUpdatesRef.current.push(update);
        setSyncStatus('saving');

        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
            sendUpdate();
        }, 2000);
    }, []);

    useEffect(() => {
        ydoc.on('update', handleUpdate);
        return () => ydoc.off('update', handleUpdate);
    }, [ydoc, handleUpdate]);


    // 2. Send ONLY the merged deltas
    const sendUpdate = useCallback(async () => {
        if (!noteId || pendingUpdatesRef.current.length === 0) return;

        // Take all pending updates and clear the buffer immediately
        // (This prevents race conditions where new typing happens while sending)
        const updatesToSend = pendingUpdatesRef.current;
        pendingUpdatesRef.current = [];

        try {
            const mergedUpdate = Y.mergeUpdates(updatesToSend);

            const formData = new FormData();

            formData.append("noteId", noteId);
            formData.append("delta", new Blob([mergedUpdate], { type: 'application/octet-stream' }));

            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/notes/update-note`, {
                method: 'POST',
                body: formData,
            } as any);

            if (!response.ok) {
                pendingUpdatesRef.current = [...updatesToSend, ...pendingUpdatesRef.current];
                setSyncStatus('error');
                return;
            }

            setSyncStatus('saved');

        } catch (error) {
            pendingUpdatesRef.current = [...updatesToSend, ...pendingUpdatesRef.current];
            setSyncStatus('error');
        }
    }, [noteId, authenticatedFetch]);

    // 3. Load Note (Standard Yjs loading logic)
    const loadNote = useCallback(async () => {
        if (!noteId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);

        try {
            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/notes/load-note`, {
                method: 'POST',
                body: { noteId }
            } as any);

            if (!response.ok) throw new Error("Failed to load");

            const responseData = await response.json();
            const data = responseData?.message || responseData;

            // Apply Server State
            if (data.yjsState && Array.isArray(data.yjsState) && data.yjsState.length > 0) {
                //console.log("...", sharedType.toString(), sharedType.toJSON(), sharedType.toDelta());
                const stateArray = new Uint8Array(data.yjsState);
                Y.applyUpdate(ydoc, stateArray, 'server');
                console.log("content", ydoc.get('content', Y.XmlText).toJSON());

                /*
                const initialValue = [{ type: 'paragraph', children: [{ text: '' }] }];
                const delta = slateNodesToInsertDelta(initialValue);
                sharedType.applyDelta(delta);

                */
                /*
                const initialValue = [{ type: 'paragraph', children: [{ text: '' }] }];
                sharedType.applyDelta(slateNodesToInsertDelta(initialValue));
                console.log("???", sharedType.toString(), sharedType.toJSON());
                */

            } else {

                /*
                if (sharedType.length === 0) {
                    const initialValue = [{ type: 'paragraph', children: [{ text: '' }] }];
                    sharedType.applyDelta(slateNodesToInsertDelta(initialValue));
                }
                */

            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [noteId, authenticatedFetch]);

    useEffect(() => { loadNote(); }, [noteId]);

    // 4. Safe Connect (Prevents the "remove event handler" error)
    useEffect(() => {
        if (isLoading || !editor) return;

        if (!YjsEditor.connected(editor)) {
            YjsEditor.connect(editor);
        }
        return () => {
            if (YjsEditor.connected(editor)) {
                YjsEditor.disconnect(editor);
            }
        };
    }, [editor, isLoading]);

    // Force flush on close
    useEffect(() => {
        window.addEventListener('beforeunload', sendUpdate);
        return () => {
            window.removeEventListener('beforeunload', sendUpdate);
            editor.selection = null;
            sendUpdate();
        }
    }, []);

    // ... Rendering logic (Slate, Editable, etc.) ...

    // (Ensure you include the rest of your render code here)
    const editorKey = useMemo(() => isLoading ? 'loading' : `ready-${noteId}`, [isLoading, noteId]);

    const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
    const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);

    if (isLoading) {
        return (
            <div className="flex flex-col justify-center items-center w-full h-full">
                <Loader2 className="size-6 animate-spin text-theme-0 mb-2" />
            </div>
        );
    }

    if (noteId === null) return;

    return (
        <div className="flex flex-col justify-center w-full h-full">
            <div className="absolute top-4 right-4 z-10">
                <SyncStatusIndicator status={syncStatus} />
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden" ref={editorContainer}>
                <div className="max-w-[640px] mx-auto px-2 py-8 relative">
                    <Slate
                        key={editorKey} // Important: Force re-render after load
                        editor={editor}
                        initialValue={[{ type: 'paragraph', children: [{ text: '' }] }]}
                    >
                        <FloatingToolbar editorContainer={editorContainer} editor={editor} />
                        <Editable
                            renderLeaf={renderLeaf}
                            renderElement={renderElement}
                            placeholder="Start writing..."
                            className="min-h-[70vh] focus:outline-none"
                        />
                    </Slate>
                </div>
            </div>
        </div>
    );
};